import BodyText from 'LuxKit/Typography/BodyText'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import React from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import type { CABIN_CATEGORY_TYPE } from 'constants/cruise'
import { CABIN_CATEGORY_DESCRIPTIONS } from 'constants/cruise'
import type { CABIN_CATEGORY_LABEL_TYPE_BOTH, CABIN_CATEGORY_LABEL_TYPE_RIVER, CabinTypeMap } from '../CruiseSelectCabinType'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import CruiseCabinTypeGrid from 'components/Cruises/Common/CruiseCabinTypeGrid'
import CruiseCabinTypeTileWrapper from 'components/Cruises/Common/CruiseCabinTypeTileWrapper'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'

interface Props {
  cabinTypeMap: CabinTypeMap

  // Cabin type labels could differ depending on the cruise types in the search results.
  cabinTypeLabels: Array<CABIN_CATEGORY_TYPE | CABIN_CATEGORY_LABEL_TYPE_RIVER | CABIN_CATEGORY_LABEL_TYPE_BOTH>
}

function CruiseCabinTypeInfoModal({ cabinTypeMap, cabinTypeLabels }: Props) {
  const intl = useIntl()
  const modalState = useModalElementContext<string>()
  const isPricePerNight = useCruisePriceByNight()
  const priceProperties = isPricePerNight ? 'minimumPricePerNight' : 'minimumPrice'

  return <Modal
    size="L"
    title={intl.formatMessage({
      id: 'MEozziSbkR',
      defaultMessage: 'Which cabin type is right for me?',
      description: 'Modal title for cabin type selection',
    })}
    subtitle={intl.formatMessage({
      id: 'g0VQ+GWi4r',
      defaultMessage: 'Find the perfect cruise cabin to match your needs.',
      description: 'Modal subtitle for cabin type selection',
    })}
  >
    <Group gap={24} direction="vertical">
      <CruiseCabinTypeGrid>
        {cabinTypeLabels.map((cabinType) => (
          <CruiseCabinTypeTileWrapper
            key={cabinType}
            direction="vertical"
            gap={12}
          >
            <VerticalSpacer gap={16}>
              <Heading variant="heading4">{cabinType}</Heading>
              <BodyText variant="small">
                {CABIN_CATEGORY_DESCRIPTIONS[cabinType]}
              </BodyText>
            </VerticalSpacer>
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
              <Group direction="vertical" verticalAlign="center" gap={2}>
                <Group direction="horizontal" verticalAlign="center" gap={2}>
                  <PriceRowPriceWithCaption
                    price={cabinTypeMap[cabinType]?.[priceProperties]}
                    size="S"
                    caption="From"
                    saleUnit={isPricePerNight ? '/night' : '/person'}
                  />
                </Group>
              </Group>

              <TextButton
                kind="secondary"
                onClick={() => modalState.resolve(cabinType)}
              >
                {intl.formatMessage({
                  id: 'TuW0NUhI2M',
                  defaultMessage: 'View',
                  description: 'Button label to view cabin type details',
                })}
              </TextButton>
            </Group>
          </CruiseCabinTypeTileWrapper>
        ))}
      </CruiseCabinTypeGrid>
    </Group>
  </Modal>
}

export default CruiseCabinTypeInfoModal
